import { PartialMaturity, PaymentRowType } from './types'

/** String constant matching PaymentRowType.DOLLAR — avoids importing the enum
 *  into a string-typed comparison which triggers no-unsafe-enum-comparison. */
const DOLLAR_TYPE: string = PaymentRowType.DOLLAR

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

export const roundToDecimals = (value: number, decimals: number): number =>
  parseFloat(value.toFixed(decimals))

export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(date)
}

export const formatDateTime = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date)
}

export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return ''
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)
}

export const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-'
  // Strip floating-point noise (e.g. 0.1 + 0.2), then trim trailing zeros to
  // discover the user-entered precision. Floor display at 2 decimals; preserve
  // any additional decimals the user supplied (no upper cap).
  const denoised = value.toFixed(10).replace(/0+$/, '').replace(/\.$/, '')
  const decimals = (denoised.split('.')[1] ?? '').length
  return `${value.toFixed(Math.max(2, decimals))}%`
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
export const getMaxDecimalPlaces = (
  values: Array<number | null | undefined>
): number => {
  let maxDecimals = 0

  for (const value of values) {
    if (value === null || value === undefined) continue

    const asString = value.toString()
    const decimalIndex = asString.indexOf('.')

    if (decimalIndex >= 0) {
      const decimals = asString.length - decimalIndex - 1
      if (decimals > maxDecimals) {
        maxDecimals = decimals
      }
    }
  }

  return Math.min(3, maxDecimals)
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
export const formatPayoutWithDecimals = (
  value: number | null | undefined,
  decimals: number
): string => {
  if (value === null || value === undefined) return '-'

  const clampedDecimals = clamp(decimals, 0, 3)
  return `${roundToDecimals(value, clampedDecimals)}%`
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
export const formatWamYears = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-'

  // Clamp to a maximum of 3 decimal places, rounding instead of truncating
  return roundToDecimals(value, 3).toString()
}

export const formatPartialMaturities = (
  maturities: PartialMaturity[] | null | undefined
): string => {
  if (maturities == null || maturities.length === 0) return '-'
  return maturities
    .map((m) => {
      const date = formatDate(m.paymentDate as string)
      const amount =
        m.inputType === DOLLAR_TYPE
          ? formatCurrency(m.amount)
          : formatPercentage(m.amount)
      return `${date}: ${amount}`
    })
    .join('; ')
}
